import HiddenInput from "@/components/form/components/HiddenInput";
import { datePickerClearableProps, datePickerFormItemProps } from "@/utils/componentUtils";
import { setErrorsToForm } from "@/utils/formUtils";
import { validations } from "@/utils/validationUtils";
import { DatePicker, Form, Modal } from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUpdateContractEndDates } from "../../../common/api/queries";
import { ModalSizes } from "../../../common/constants";
import { ContractType, PeriodType } from "../enums";
import { Contract, Insurance, UpdateContractEndDates } from "../types";

const getLatestInsuranceContractDate = (insurances: Insurance[], fallbackDate: string): Dayjs => {
  return insurances.reduce((latestDate, insurance) => {
    return [insurance.contractEntryDate, insurance.contractWithdrawalDate]
      .filter((date): date is string => !!date)
      .reduce((latestInsuranceDate, date) => {
        const currentDate = dayjs(date);

        return currentDate.isAfter(latestInsuranceDate, "day") ? currentDate : latestInsuranceDate;
      }, latestDate);
  }, dayjs(fallbackDate));
};

const getCancellationMaxDate = (contract: Contract, effectiveEndDate?: string) => {
  const endDate = effectiveEndDate ? dayjs(effectiveEndDate).subtract(1, "day") : undefined;

  if (contract.type === ContractType.LOAN_CONTRACT) {
    const loanMaturityDate = dayjs(contract.loanMaturityDate).subtract(1, "day");

    return endDate?.isBefore(loanMaturityDate) ? endDate : loanMaturityDate;
  }

  return endDate;
};

type Props = {
  isOpen: boolean;
  onCancel: () => void;
  contract: Contract;
};

export const ContractCancellationEffectiveEndDateModalForm = ({ isOpen, onCancel, contract }: Props) => {
  const { t } = useTranslation();

  const updateContractEndDates = useUpdateContractEndDates();

  const [form] = Form.useForm<UpdateContractEndDates>();
  const effectiveEndDate = Form.useWatch("effectiveEndDate", form);

  const hasEffectiveEndDate =
    contract.type === ContractType.DEPOSIT_CONTRACT ||
    contract.type === ContractType.INSURANCE_CONTRACT ||
    contract.type === ContractType.INVESTMENT_CONTRACT;

  const latestInsuranceContractDate =
    contract.type === ContractType.INSURANCE_CONTRACT
      ? getLatestInsuranceContractDate(contract.insurances, contract.effectiveBeginningDate)
      : undefined;

  useEffect(() => {
    if (isOpen) {
      form.setFieldsValue({
        optimisticLockVersion: contract.optimisticLockVersion,
        cancellationDate: contract.cancellationDate,
        effectiveEndDate: hasEffectiveEndDate ? contract.effectiveEndDate : undefined,
      });
    }
  }, [contract, form, hasEffectiveEndDate, isOpen]);

  useEffect(() => {
    if (form.isFieldTouched("effectiveEndDate")) {
      form.validateFields(["cancellationDate"]);
    }
  }, [effectiveEndDate, form]);

  const handleFormSubmit = () => {
    form.validateFields().then((values) => {
      updateContractEndDates.mutate(
        { id: contract.id, object: values },
        {
          onSuccess: onCancel,
          onError: (error) => setErrorsToForm(form, "contract.attrs", error.violations),
        },
      );
    });
  };

  return (
    <Modal
      width={ModalSizes.TINY}
      open={isOpen}
      title={t("contract.titles.updateEndDates")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={updateContractEndDates.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onCancel}
      mask={{ closable: false }}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" name="contractCancellationEffectiveEndDateModalForm">
        <HiddenInput name="optimisticLockVersion" />

        <Form.Item
          name="effectiveEndDate"
          label={t("contract.attrs.effectiveEndDate")}
          rules={
            contract.type === ContractType.INSURANCE_CONTRACT
              ? contract.periodType === PeriodType.DEFINITE
                ? [
                    validations.notNull,
                    validations.notBefore(contract.effectiveBeginningDate, t("contract.attrs.effectiveBeginningDate")),
                    ...(latestInsuranceContractDate ? [validations.notBefore(latestInsuranceContractDate)] : []),
                  ]
                : [validations.none]
              : [validations.notSameOrBefore(contract.signDate, t("contract.attrs.signDate"))]
          }
          {...datePickerFormItemProps}
        >
          <DatePicker
            {...datePickerClearableProps}
            disabled={
              !hasEffectiveEndDate ||
              (contract.type === ContractType.INSURANCE_CONTRACT && contract.periodType === PeriodType.INDEFINITE)
            }
            minDate={
              contract.type === ContractType.INSURANCE_CONTRACT
                ? latestInsuranceContractDate
                : dayjs(contract.signDate).add(1, "day")
            }
          />
        </Form.Item>

        <Form.Item
          name="cancellationDate"
          label={t("contract.attrs.cancellationDate")}
          rules={[
            ...(contract.type === ContractType.LOAN_CONTRACT
              ? [validations.notSameOrAfter(contract.loanMaturityDate, t("contract.attrs.loanMaturityDate"))]
              : []),
            ...(contract.type === ContractType.INSURANCE_CONTRACT
              ? [validations.notBefore(contract.effectiveBeginningDate, t("contract.attrs.effectiveBeginningDate"))]
              : [validations.notBefore(contract.signDate, t("contract.attrs.signDate"))]),
            effectiveEndDate
              ? validations.notSameOrAfter(effectiveEndDate, t("contract.attrs.effectiveEndDate"))
              : validations.none,
          ]}
          {...datePickerFormItemProps}
        >
          <DatePicker
            {...datePickerClearableProps}
            maxDate={getCancellationMaxDate(contract, effectiveEndDate)}
            minDate={dayjs(
              contract.type === ContractType.INSURANCE_CONTRACT ? contract.effectiveBeginningDate : contract.signDate,
            )}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
